import { ChevronDown } from "lucide-react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/baseUi/dropdown-menu"
import { Text } from "@/components/ui/baseUi/text"
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@/components/ui/layout/menuSidebar/sidebar"
import { LanguagePopup } from "@/modules/app/components/menu/profile/languagePopup"
import { useDropdownMenuModel } from "@/modules/app/hooks/useMenuModel"
import { useCurrentBox } from "@/modules/box/hooks/useBox"
import { useCurrentCoachIfExists } from "@/modules/coach/hooks/useProfessionalCoach"
import { useCurrentCoachId } from "@/modules/core/store/useCurrentSpaceManagerStore"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { ProfileAvatar } from "@/modules/profile/common/components/profileAvatar"
import { useUser } from "@/modules/users/hooks/useUser"
import { useState } from "react"

export function NavFooter() {
  const [displayDialog, setDisplayDialog] = useState(false)
  const user = useUser()
  const { result: currentBox } = useCurrentBox()
  const { currentCoach } = useCurrentCoachIfExists()
  const currentCoachId = useCurrentCoachId()
  const { mySpaceOption, boxOptions, coachesOptions, settingOptions, logoutOption } = useDropdownMenuModel(
    () =>
      setTimeout(() => {
        setDisplayDialog(true)
      }, 100), // Quick hack to let dropdown menu close before opening the language popup - otherwise the event pointer is not released when closing the language popup
  )

  return (
    <SidebarMenu>
      <SidebarMenuItem className="hover:interactive-background-neutral-highlighted border-t border-b border-surface-border-gray-default">
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <SidebarMenuButton
                size="lg"
                variant="ghost"
                className="group/buttonFooter h-14 cursor-pointer rounded-none group-data-[collapsible=icon]:overflow-visible group-data-[collapsible=icon]:p-4!"
              >
                <ProfileAvatar
                  profile={
                    currentBox
                      ? { avatar: currentBox.logo, username: currentBox.name }
                      : currentCoachId && currentCoach
                        ? { avatar: currentCoach.state.profilePhoto, username: currentCoach.getCoachFullName() }
                        : { avatar: user.avatar ?? null, username: user.username }
                  }
                  className="size-8"
                />
                <div className="grid flex-1">
                  <Text fontStyle="LL" className="truncate text-start overflow-ellipsis">
                    {currentBox
                      ? currentBox.name
                      : currentCoachId && currentCoach
                        ? currentCoach.getCoachFullName()
                        : `${user.firstname} ${user.lastname}`}
                  </Text>
                  <Text fontStyle="BS" className="truncate text-start text-neutral-600">
                    {currentBox
                      ? word("menu.profile.box.type")
                      : currentCoachId && currentCoach
                        ? word("menu.profile.coach.type")
                        : word("menu.profile.mySpace.type")}
                  </Text>
                </div>
                <ChevronDown className="ml-auto size-4 transition-transform duration-200 group-data-popup-open/buttonFooter:rotate-180 group-data-[collapsible=icon]:hidden" />
              </SidebarMenuButton>
            }
          />
          <DropdownMenuContent
            className="min-w-80 overflow-hidden rounded-3xl border-none bg-surface-background-float p-0"
            side="bottom"
            align="start"
            sideOffset={5}
          >
            <div className="no-scrollbar max-h-130 overflow-y-auto">
              <DropdownMenuGroup>
                <DropdownMenuItem className="p-0 pt-2">
                  <NavFooterItem
                    icon={mySpaceOption.icon}
                    title={mySpaceOption.title}
                    subTitle={mySpaceOption.subTitle}
                    profileTypeIcon={mySpaceOption.profileTypeIcon}
                    selected={mySpaceOption.selected}
                    onPress={mySpaceOption.onPress}
                  />
                </DropdownMenuItem>
              </DropdownMenuGroup>

              <DropdownMenuSeparator className="mx-2 my-0" />

              <DropdownMenuGroup>
                {coachesOptions.map((coachOption, index) => (
                  <DropdownMenuItem key={index} className="p-0">
                    <NavFooterItem
                      icon={coachOption.icon}
                      title={coachOption.title}
                      subTitle={coachOption.subTitle}
                      profileTypeIcon={coachOption.profileTypeIcon}
                      selected={coachOption.selected}
                      onPress={coachOption.onPress}
                    />
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>

              <DropdownMenuSeparator className="mx-2 my-0" />

              <DropdownMenuGroup>
                {boxOptions.map((boxOption, index) => (
                  <DropdownMenuItem key={index} className="p-0">
                    <NavFooterItem
                      icon={boxOption.icon}
                      title={boxOption.title}
                      subTitle={boxOption.subTitle}
                      profileTypeIcon={boxOption.profileTypeIcon}
                      selected={boxOption.selected}
                      onPress={boxOption.onPress}
                    />
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>

              <DropdownMenuSeparator className="mx-2 my-0" />

              <DropdownMenuGroup>
                {settingOptions.map((settingOption, index) => (
                  <DropdownMenuItem key={index} className="p-0">
                    <NavFooterItem
                      icon={settingOption.icon}
                      title={settingOption.title}
                      onPress={settingOption.onPress}
                    />
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>

              <DropdownMenuSeparator className="mx-2 my-0" />

              <DropdownMenuGroup>
                <DropdownMenuItem className="p-0 pb-1">
                  <NavFooterItem icon={logoutOption.icon} title={logoutOption.title} onPress={logoutOption.onPress} />
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
      <LanguagePopup onOpenChange={setDisplayDialog} visible={displayDialog} />
    </SidebarMenu>
  )
}

type NavFooterItemProps = {
  icon?: React.ReactNode
  title: string | undefined
  subTitle?: string
  profileTypeIcon?: React.ReactNode
  selected?: boolean
  onPress: (e: React.SyntheticEvent) => void
} & React.ComponentProps<"button">

export function NavFooterItem({
  icon,
  title,
  subTitle,
  profileTypeIcon,
  selected,
  onPress,
  ...props
}: NavFooterItemProps) {
  return (
    <button
      onClick={onPress}
      className={cn(
        "transition-duration-150 w-full cursor-pointer px-2 py-1 transition-colors hover:bg-interactive-background-neutral-highlighted",
        props.className,
      )}
      {...props}
    >
      <div className="flex h-11 flex-row items-center gap-0 pr-0.5">
        <div className="flex min-w-13 justify-center">
          <div
            className={cn(
              "rounded-full border-2 border-transparent p-0.5",
              selected && "border-interactive-background-primary-default",
            )}
          >
            <div className="flex size-8 items-center justify-center bg-transparent">{icon}</div>
          </div>
        </div>
        <div className="flex max-w-54 flex-col gap-0">
          <Text fontStyle="LL" className="truncate text-start">
            {title}
          </Text>
          {subTitle && (
            <Text fontStyle="BS" className="truncate text-start text-neutral-600">
              {subTitle}
            </Text>
          )}
        </div>
        {profileTypeIcon && (
          <div className="ml-auto flex size-6 shrink-0 items-center justify-center">{profileTypeIcon}</div>
        )}
      </div>
    </button>
  )
}
